import { z } from 'zod'
import { SystemAccessInputSchema, SystemAccessViewSchema } from './sharing.ts'

/**
 * The remote-agent registry and delegation grants, as the admin API and the SPA see them.
 *
 * A2A wire types are deliberately NOT here: `apps/api/src/infrastructure/a2a/types.ts` owns
 * those and nothing outside the gateway imports them. The split is the point — everything
 * above the gateway talks about governance objects (is this agent enabled, what may it be
 * told, for how long) and never about protocol envelopes, so a protocol revision changes one
 * module inside the gateway rather than every consumer that compiles against these shapes
 * (PRD-05 A2A-030).
 */

export const REMOTE_AGENT_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,62}$/
export const RemoteAgentSlugSchema = z
  .string()
  .regex(REMOTE_AGENT_SLUG_PATTERN, 'slug must be lowercase alphanumeric with dashes, 2-63 characters')

/**
 * How much of the card may be believed.
 *
 * `untrusted` is the default and stays the default after every card refresh: a trust tier is
 * an administrator's assessment of a publisher, and no field a remote agent serves about
 * itself can raise it (A2A-005).
 */
export const RemoteAgentTrustTierSchema = z.enum(['untrusted', 'reviewed', 'first_party'])
export type RemoteAgentTrustTier = z.infer<typeof RemoteAgentTrustTierSchema>

/** The token itself is never stored; `tokenRef` is an `env:NAME` indirection resolved at call time. */
export const RemoteAgentAuthKindSchema = z.enum(['none', 'bearer'])
export type RemoteAgentAuthKind = z.infer<typeof RemoteAgentAuthKindSchema>

/**
 * A skill as the card advertises it.
 *
 * There is no input schema here and that is the protocol's design, not an omission: A2A has
 * no "call this skill" operation. You send a message and the agent decides. Anything that
 * looks like a capability guarantee is publisher-authored prose.
 */
export const RemoteAgentSkillSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  tags: z.array(z.string())
})
export type RemoteAgentSkill = z.infer<typeof RemoteAgentSkillSchema>

export const RemoteAgentSchema = z.object({
  id: z.uuid(),
  slug: RemoteAgentSlugSchema,
  cardUrl: z.url(),
  /** From the card, so it is empty until the first successful fetch. */
  name: z.string(),
  description: z.string(),
  trustTier: RemoteAgentTrustTierSchema,
  enabled: z.boolean(),
  /** Who may have their agents delegate to it (PRD-26 D-25): everyone, or specific groups. */
  access: SystemAccessViewSchema,
  authKind: RemoteAgentAuthKindSchema,
  tokenRef: z.string().nullable(),
  protocolVersion: z.string().nullable(),
  /** The REST interface chosen from the card's ordered `supportedInterfaces`. */
  interfaceUrl: z.string().nullable(),
  cardDigest: z.string(),
  cardFetchedAt: z.iso.datetime().nullable(),
  skills: z.array(RemoteAgentSkillSchema),
  version: z.number().int(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime()
})
export type RemoteAgent = z.infer<typeof RemoteAgentSchema>

export const RegisterRemoteAgentRequestSchema = z
  .object({
    slug: RemoteAgentSlugSchema,
    cardUrl: z.url(),
    trustTier: RemoteAgentTrustTierSchema.optional(),
    access: SystemAccessInputSchema.optional(),
    authKind: RemoteAgentAuthKindSchema.optional(),
    tokenRef: z.string().nullish()
  })
  .strict()
export type RegisterRemoteAgentRequest = z.infer<typeof RegisterRemoteAgentRequestSchema>

/**
 * `enabled` is absent on purpose. Enabling is its own endpoint because it requires naming the
 * card digest being accepted, and folding it into a general update would let an operator turn
 * an agent back on without seeing what changed.
 */
export const UpdateRemoteAgentRequestSchema = z
  .object({
    cardUrl: z.url().optional(),
    trustTier: RemoteAgentTrustTierSchema.optional(),
    access: SystemAccessInputSchema.optional(),
    authKind: RemoteAgentAuthKindSchema.optional(),
    tokenRef: z.string().nullish()
  })
  .strict()
export type UpdateRemoteAgentRequest = z.infer<typeof UpdateRemoteAgentRequestSchema>

/** The digest the operator reviewed. A mismatch means the card moved under them. */
export const EnableRemoteAgentRequestSchema = z.object({ cardDigest: z.string().min(1) }).strict()

export const RefreshAgentCardResponseSchema = z.object({
  agent: RemoteAgentSchema,
  cardChanged: z.boolean(),
  /** True when a changed card forced the agent off and a human has to re-enable it. */
  requiresReview: z.boolean(),
  warnings: z.array(z.string())
})
export type RefreshAgentCardResponse = z.infer<typeof RefreshAgentCardResponseSchema>

/**
 * What a remote agent may be told and may ask for, per purpose.
 *
 * `allowedLocalCapabilities` defaults to empty and that default is the security posture, not
 * a placeholder: a remote agent gets no local authority, so anything it asks the platform to
 * do arrives as a proposal for the local approval path rather than as an execution (A2A-018).
 */
export const DelegationGrantSchema = z.object({
  id: z.uuid(),
  remoteAgentId: z.uuid(),
  purpose: z.string(),
  allowedInputClasses: z.array(z.string()),
  allowedLocalCapabilities: z.array(z.string()),
  maxCostUsd: z.number(),
  maxDurationMs: z.number().int(),
  maxDepth: z.number().int(),
  requiresApproval: z.boolean(),
  expiresAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  revokedAt: z.iso.datetime().nullable(),
  /** Neither revoked nor expired, evaluated server-side so a stale clock in a browser cannot widen it. */
  active: z.boolean()
})
export type DelegationGrant = z.infer<typeof DelegationGrantSchema>

export const CreateDelegationGrantRequestSchema = z
  .object({
    purpose: z.string().min(1).max(500),
    allowedInputClasses: z.array(z.string()).default([]),
    allowedLocalCapabilities: z.array(z.string()).default([]),
    maxCostUsd: z.number().nonnegative().default(0),
    maxDurationMs: z
      .number()
      .int()
      .positive()
      .max(24 * 60 * 60 * 1000)
      .default(900_000),
    maxDepth: z.number().int().min(0).max(3).default(1),
    requiresApproval: z.boolean().default(true),
    expiresAt: z.iso.datetime().nullish()
  })
  .strict()
export type CreateDelegationGrantRequest = z.infer<typeof CreateDelegationGrantRequestSchema>

/**
 * An artifact the remote agent produced, described rather than embedded.
 *
 * Bytes are not carried here. An artifact is a deliverable with provenance and a scan status
 * (A2A-019), so admitting one into the platform is the artifact service's decision, not the
 * delegation call's.
 */
export const RemoteArtifactRefSchema = z.object({
  name: z.string(),
  mediaType: z.string(),
  bytes: z.number().int().nonnegative()
})
export type RemoteArtifactRef = z.infer<typeof RemoteArtifactRefSchema>

export const DelegationOutcomeSchema = z.object({
  ok: z.boolean(),
  summary: z.string(),
  content: z.string(),
  artifacts: z.array(RemoteArtifactRefSchema)
})
export type DelegationOutcome = z.infer<typeof DelegationOutcomeSchema>
